import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import tinycolor from "tinycolor2";
import SVG from "react-inlinesvg";
import * as Immutable from "immutable";
import { Client } from "resourceModels";
import SvgIcon from "components/Common/SvgIcon";
import colors from "stylesheets/utilities/colors.scss";
import "./style.scss";

export default class Avatar extends React.PureComponent {
  /**
   * Render the Avatar letter
   *
   * @param {Immutable.Map} clientBotInfo
   *
   * @returns {ReactElement}
   */
  static renderAvatarLetter(clientBotInfo) {
    const clientLetter = clientBotInfo.getIn(["avatar", "letter"]);

    return (
      <div
        className="Avatar__letter"
      >
        {clientLetter}
      </div>
    );
  }

  /**
   * Render the Avatar image
   *
   * @param {Immutable.Map} clientBotInfo
   *
   * @returns {ReactElement}
   */
  static renderAvatarImage(clientBotInfo) {
    const clientImage = clientBotInfo.getIn(["avatar", "image"]);

    return (
      // SVG will render the src svg image inline, allowing us to change fill colour
      <SVG
        src={clientImage || ""}
        className="Avatar__image"
      />
    );
  }

  /**
   * Render the Agent Avatar image
   *
   * @param {String} imageUrl
   * @param {String} agentName
   *
   * @returns {ReactElement}
   */
  static renderAgentImage(imageUrl, agentName) {
    return (
      <img
        className="Avatar__agent__image"
        src={imageUrl}
        alt={`${agentName}'s Avatar`}
      />
    );
  }

  /**
   * Render the Avatar for default Agent
   * @returns {ReactElement}
   */
  renderAgentAvatar() {
    const {
      /** @type Client */
      client,
      agentInfo
    } = this.props;

    const customAvatar = client.agentAvatar && !client.agentAvatar.get("default")
      ? client.agentAvatar.get("image")
      : null;
    const hasAgentInfo = Boolean(agentInfo);
    const defaultImage = hasAgentInfo && agentInfo.get("defaultImage");
    const imageUrl = hasAgentInfo ? agentInfo.get("imageUrl") : "";
    const agentName = hasAgentInfo ? agentInfo.get("name") : "Agent";

    if (customAvatar) {
      return Avatar.renderAgentImage(customAvatar, agentName);
    }

    if (!hasAgentInfo || defaultImage || !imageUrl) {
      return (
        <SvgIcon
          icon="Agent"
          customClassName="Avatar__agent__image"
          fillColor={colors.colorWhite}
        />
      );
    }

    return Avatar.renderAgentImage(imageUrl, agentName);
  }

  /**
   * Determine if letter, image, or othing should be rendered
   *
   * @returns {ReactElement|null}
   */
  render() {
    const {
      /** @type Client */
      client,
      isLarge,
      clientToken,
      customClassName,
      source
    } = this.props;

    const clientBotInfo = (
      client.routingInfo.get(clientToken) ||
      Immutable.Map(client.attributes).merge(client.changedAttributes)
    );

    const useLetter = clientBotInfo.getIn(["avatar", "useLetter"]);
    const clientTint = clientBotInfo.get("tint");
    const color = tinycolor(clientTint).isDark() ? "white" : null;
    const style = {
      backgroundColor: clientTint,
      borderColor: clientTint,
      color,
      fill: color
    };
    const isAgent = source === "agent";
    const className = classnames(
      "Avatar",
      {
        [customClassName]: Boolean(customClassName),
        "Avatar--large": isLarge,
        "Avatar__agent": isAgent
      }
    );

    let innerHTML;

    if (isAgent) {
      innerHTML = this.renderAgentAvatar();
    } else if (useLetter) {
      innerHTML = Avatar.renderAvatarLetter(clientBotInfo);
    } else {
      innerHTML = Avatar.renderAvatarImage(clientBotInfo);
    }

    return (
      <div
        className={className}
        style={style}
      >
        {innerHTML}
      </div>
    );
  }
}

Avatar.defaultProps = {
  customClassName: null,
  clientToken: null,
  isLarge: false,
  source: null,
  agentInfo: null
};

Avatar.propTypes = {
  client: PropTypes.instanceOf(Client).isRequired,
  customClassName: PropTypes.string,
  clientToken: PropTypes.string,
  isLarge: PropTypes.bool,
  source: PropTypes.string,
  agentInfo: PropTypes.shape({
    name: PropTypes.string,
    external_id: PropTypes.number,
    internal_id: PropTypes.number,
    default_image: PropTypes.bool,
    image_url: PropTypes.string
  })
};
